import { Button, Stack } from '@mui/material';
import { isEmpty, map } from 'lodash';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';

import { orderAutomationApi } from 'api';
import {
  OrderAutomationStatusLabel,
  CopyText,
  DescriptionRow,
  DateLabel,
  RequisitesInfo,
  Dialog,
  ParsingMethodsMultipleForm,
  CloseDialogResult,
  ConfirmButton,
} from 'components';
import { DateFormat, OrderAutomationStatus } from 'enums';
import { useMutation, useUser, useUserContextUtils } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { OrderAutomation, PaginatedData, ParsingMethod } from 'types';
import { formatUtils, orderAutomationUtils } from 'utils';

import { OrderAutomationReviewDialog } from './OrderAutomationReviewDialog';
import { OrderAutomationSummary } from './OrderAutomationSummary';

type Props = {
  queryResult: UseQueryResult<PaginatedData<OrderAutomation>>;
  orderAutomation: OrderAutomation;
  onClose: () => void;
};

export const OrderAutomationDetails: React.FC<Props> = ({
  queryResult,
  orderAutomation,
  onClose,
}: Props) => {
  const { user, isAdmin, isTechOperator } = useUser();
  const { getBankName, getPaymentTypeLabel } = useUserContextUtils();

  const [isParsingMethodsDialogOpen, setParsingMethodsDialogOpen] =
    useState(false);
  const [
    isOrderAutomationReviewDialogOpen,
    setIsOrderAutomationReviewDialogOpen,
  ] = useState(false);

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.order_automation',
  });
  const { t: tCommon } = useTranslation();
  const { t: tColumns } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.order_automation.table.columns',
  });
  const { t: tParsingMethods } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.parsing_methods',
  });

  const bankId = useMemo(
    () => orderAutomation.requisites?.bankId || orderAutomation.bankId,
    [orderAutomation],
  );

  const onAddParsingMethodsDialog = useCallback(() => {
    setParsingMethodsDialogOpen(true);
  }, []);

  const onCloseParsingMethodsDialog = useCallback(() => {
    setParsingMethodsDialogOpen(false);
  }, []);

  const { mutate: applyErrorTemplate } = useMutation(
    orderAutomationApi.applyErrorTemplateAsRole(user.role),
    {
      onSuccess: () => {
        queryResult.refetch();
        onClose();
      },
    },
  );

  const { mutate: markAsSpam } = useMutation(
    orderAutomationApi.markAsSpamAsRole(user.role),
    {
      onSuccess: () => {
        queryResult.refetch();
        onClose();
      },
    },
  );

  const handleParsingMethodCreateSuccess = useCallback(
    (parsingMethods: ParsingMethod[], applyToAll: boolean) => {
      setParsingMethodsDialogOpen(false);
      if (parsingMethods.length) {
        applyErrorTemplate({
          orderAutomationId: orderAutomation.id,
          parsingMethodId: parsingMethods[0].id, // apply first as template is the same for all
          applyToAll,
        });
      } else {
        // eslint-disable-next-line no-console
        console.error(
          'Failed to apply template error as no parsing method id passed',
          { parsingMethods },
        );
      }
    },
    [applyErrorTemplate, orderAutomation.id],
  );

  const handleOrderAutomationReviewDialogClose = useCallback(
    (result: CloseDialogResult<any>) => {
      if (result.ok) {
        queryResult.refetch();
        onClose();
      }
      setIsOrderAutomationReviewDialogOpen(false);
    },
    [queryResult, onClose],
  );

  return (
    <Stack direction="column" spacing={3}>
      <DescriptionRow
        title={tColumns('created_at')}
        value={
          <DateLabel>
            {formatUtils.formatDate(orderAutomation.createdAt, DateFormat.Full)}
          </DateLabel>
        }
      />
      <DescriptionRow
        title={tColumns('id')}
        value={orderAutomation.id}
        canCopy
      />
      <DescriptionRow
        title={tColumns('status')}
        value={
          <Fragment>
            <OrderAutomationStatusLabel status={orderAutomation.status} />
            {orderAutomation.statusDetails && (
              <div>
                {orderAutomationUtils.getStatusDetailsLabel(
                  orderAutomation.statusDetails,
                )}
              </div>
            )}
          </Fragment>
        }
      />
      {orderAutomation.status === OrderAutomationStatus.Reviewed && (
        <DescriptionRow
          title={tColumns('reviewed')}
          value={
            <Fragment>
              {orderAutomation.reviewedBy?.name && (
                <div>{orderAutomation.reviewedBy.name}</div>
              )}
              {orderAutomation.reviewedAt && (
                <div>{formatUtils.formatDate(orderAutomation.reviewedAt)}</div>
              )}
            </Fragment>
          }
        />
      )}
      {orderAutomation.initialStatus && (
        <DescriptionRow
          title={tColumns('initial_status')}
          value={
            <OrderAutomationStatusLabel
              status={orderAutomation.initialStatus}
            />
          }
        />
      )}
      {orderAutomation.conflictedOrdersIds && (
        <DescriptionRow
          title={tColumns('conflicted_orders')}
          value={map(orderAutomation.conflictedOrdersIds, (id) => (
            <CopyText text={id} key={id} />
          ))}
        />
      )}
      {orderAutomation.order?.id && (
        <DescriptionRow
          title={tColumns('order')}
          value={<CopyText text={orderAutomation.order.id} />}
        />
      )}
      {orderAutomation.order?.shop && (
        <DescriptionRow
          title={tColumns('shop')}
          value={
            <Fragment>
              <CopyText text={orderAutomation.order.shop.id} />
              <div>{formatUtils.formatName(orderAutomation.order.shop)}</div>
            </Fragment>
          }
        />
      )}
      {orderAutomation.order?.merchant?.user && (
        <DescriptionRow
          title={tColumns('merchant')}
          value={
            <Fragment>
              <CopyText text={orderAutomation.order.merchant.user.id} />
              <div>{orderAutomation.order.merchant.user.name}</div>
            </Fragment>
          }
        />
      )}
      {orderAutomation.trader && (
        <DescriptionRow
          title={tColumns('trader')}
          value={
            <Fragment>
              <CopyText text={orderAutomation.trader.id} />
              <div>{orderAutomation.trader.user?.name}</div>
            </Fragment>
          }
        />
      )}
      {orderAutomation.requisitesGroup && (
        <DescriptionRow
          title={tColumns('requisites_group')}
          value={
            <Fragment>
              <CopyText text={orderAutomation.requisitesGroup.id} />
              <div>{orderAutomation.requisitesGroup.name}</div>
            </Fragment>
          }
        />
      )}
      {orderAutomation.requisites && (
        <DescriptionRow
          title={tColumns('requisites')}
          value={<RequisitesInfo requisite={orderAutomation.requisites} />}
          canCopy
        />
      )}
      {bankId && (
        <DescriptionRow
          title={tColumns('bank')}
          value={
            <Fragment>
              <CopyText text={bankId} />
              <div>{getBankName({ id: bankId })}</div>
            </Fragment>
          }
        />
      )}
      {orderAutomation.requisites?.paymentTypeId && (
        <DescriptionRow
          title={tColumns('payment_type')}
          value={
            <Fragment>
              <CopyText text={orderAutomation.requisites.paymentTypeId} />
              <div>
                {getPaymentTypeLabel({
                  id: orderAutomation.requisites.paymentTypeId,
                })}
              </div>
            </Fragment>
          }
        />
      )}
      {orderAutomation.automationSourceName && (
        <DescriptionRow
          title={tColumns('automation_sources')}
          value={<CopyText text={orderAutomation.automationSourceName} />}
        />
      )}
      {orderAutomation.notificationType && (
        <DescriptionRow
          title={tColumns('notification_type')}
          value={
            <div>
              {t(`notification_types.${orderAutomation.notificationType}`)}
            </div>
          }
        />
      )}
      {orderAutomation.parsingMethod?.id && (
        <DescriptionRow
          title={tColumns('parsing_method')}
          value={
            <Fragment>
              <CopyText text={orderAutomation.parsingMethod.id} />
              <div>{orderAutomation.parsingMethod.template}</div>
            </Fragment>
          }
        />
      )}
      {orderAutomation.parsingMethodsIds && (
        <DescriptionRow
          title={tColumns('parsing_method_ids')}
          value={map(orderAutomation.parsingMethodsIds, (id) => (
            <CopyText text={id} key={id} />
          ))}
        />
      )}
      {!isEmpty(orderAutomation.parsedParams) && (
        <DescriptionRow
          title={tColumns('parsed_params')}
          value={
            <div className="tw-table-auto">
              {map(orderAutomation.parsedParams, (value, key) => (
                <div className="tw-table-row" key={key}>
                  <div className="tw-table-cell">{key}</div>
                  <div className="tw-table-cell tw-pl-4 tw-break-all">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          }
        />
      )}
      {(isAdmin || isTechOperator) && (
        <Fragment>
          {orderAutomation.status === OrderAutomationStatus.TemplateError && (
            <Stack direction="column" spacing={3}>
              <Button
                size="medium"
                variant="outlined"
                color="primary"
                sx={{ mt: '2' }}
                onClick={onAddParsingMethodsDialog}
              >
                {t('details.template_button')}
              </Button>
              <ConfirmButton
                size="medium"
                variant="outlined"
                color="primary"
                sx={{ mt: '2' }}
                onConfirm={() => markAsSpam(orderAutomation.id)}
                dialog={{
                  title: t('mark_as_spam_dialog.title'),
                  children: (
                    <Fragment>
                      <div className="tw-mb-4">
                        <span>{t('mark_as_spam_dialog.description')}</span>
                      </div>
                      <OrderAutomationSummary
                        orderAutomation={orderAutomation}
                      />
                    </Fragment>
                  ),
                }}
              >
                {t('details.mark_as_spam_button')}
              </ConfirmButton>
            </Stack>
          )}
          {orderAutomation.status === OrderAutomationStatus.NotFound && (
            <Button
              size="medium"
              variant="outlined"
              color="primary"
              sx={{ mt: '2' }}
              onClick={() => setIsOrderAutomationReviewDialogOpen(true)}
            >
              {tCommon('buttons.review')}
            </Button>
          )}
        </Fragment>
      )}
      <OrderAutomationReviewDialog
        open={isOrderAutomationReviewDialogOpen}
        data={orderAutomation}
        onClose={handleOrderAutomationReviewDialogClose}
      />
      <Dialog
        title={tParsingMethods('details_form.title.create')}
        open={isParsingMethodsDialogOpen}
        onClose={onCloseParsingMethodsDialog}
        maxWidth="sm"
        modal
      >
        <ParsingMethodsMultipleForm
          bankId={bankId}
          orderAutomation={orderAutomation}
          onSuccess={handleParsingMethodCreateSuccess}
        />
      </Dialog>
    </Stack>
  );
};
